package com.rainbowclubs.rainbow.presentation.events

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.rainbowclubs.rainbow.data.models.AttendeeStatus
import com.rainbowclubs.rainbow.data.models.Event
import com.rainbowclubs.rainbow.data.requests.AddEventCommentRequest
import com.rainbowclubs.rainbow.data.requests.UpdateEventAttendeeRequest
import com.rainbowclubs.rainbow.domain.use_cases.events.AddEventCommentUseCase
import com.rainbowclubs.rainbow.domain.use_cases.events.GetEventByIdUseCase
import com.rainbowclubs.rainbow.domain.use_cases.events.UpdateEventAttendeeUseCase
import com.rainbowclubs.rainbow.domain.use_cases.user.GetUserUseCase
import com.rainbowclubs.rainbow.presentation.ScreenState
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.receiveAsFlow
import kotlinx.coroutines.launch
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import org.jetbrains.compose.resources.StringResource
import rainbowclubs.composeapp.generated.resources.Res
import rainbowclubs.composeapp.generated.resources.comment_send_error
import kotlin.time.Clock
import kotlin.time.Instant

data class EventDetailsUiState(
    val event: Event,
    val commentText: String = "",
    val isRefreshing: Boolean = false,
    val isSendingComment: Boolean = false,
    val commentError: StringResource? = null,
    val rsvpLoadingStatus: AttendeeStatus? = null,
)

class EventDetailsViewModel(
    private val eventId: String,
    private val getEventByIdUseCase: GetEventByIdUseCase,
    private val addEventCommentUseCase: AddEventCommentUseCase,
    private val updateEventAttendeeUseCase: UpdateEventAttendeeUseCase,
    getUserUseCase: GetUserUseCase,
) : ViewModel() {

    sealed class NavigationEvent {
        object Back : NavigationEvent()
    }

    private val currentUserId = getUserUseCase()?.id

    private val _screenState =
        MutableStateFlow<ScreenState<EventDetailsUiState>>(ScreenState.Loading)
    val screenState: StateFlow<ScreenState<EventDetailsUiState>> = _screenState.asStateFlow()

    private val navigationIntentChannel = Channel<NavigationEvent>(Channel.BUFFERED)
    val navigationIntent: Flow<NavigationEvent> = navigationIntentChannel.receiveAsFlow()

    init {
        fetchEvent()
    }

    fun fetchEvent() {
        viewModelScope.launch {
            _screenState.value = ScreenState.Loading
            getEventByIdUseCase(eventId)
                .onSuccess { event ->
                    _screenState.value = ScreenState.Content(
                        EventDetailsUiState(event = event.sortedComments())
                    )
                }
                .onFailure {
                    _screenState.value = ScreenState.Error(it)
                }
        }
    }

    fun refreshEvent() {
        val currentState = _screenState.value as? ScreenState.Content
        val currentData = currentState?.data
        if (currentData == null) {
            fetchEvent()
            return
        }

        viewModelScope.launch {
            _screenState.value = ScreenState.Content(currentData.copy(isRefreshing = true))
            getEventByIdUseCase(eventId)
                .onSuccess { event ->
                    _screenState.value = ScreenState.Content(
                        currentData.copy(
                            event = event.sortedComments(),
                            isRefreshing = false,
                            commentError = null,
                        )
                    )
                }
                .onFailure {
                    _screenState.value = ScreenState.Content(
                        currentData.copy(isRefreshing = false)
                    )
                }
        }
    }

    fun onCommentChanged(value: String) {
        updateContent {
            it.copy(
                commentText = value,
                commentError = null,
            )
        }
    }

    fun sendComment() {
        val currentData = (_screenState.value as? ScreenState.Content)?.data ?: return
        val comment = currentData.commentText.trim()
        if (comment.isBlank() || currentData.isSendingComment) return

        viewModelScope.launch {
            _screenState.value = ScreenState.Content(
                currentData.copy(
                    isSendingComment = true,
                    commentError = null,
                )
            )

            addEventCommentUseCase(
                eventId = eventId,
                request = AddEventCommentRequest(commentText = comment),
            ).onSuccess { updatedEvent ->
                _screenState.value = ScreenState.Content(
                    currentData.copy(
                        event = updatedEvent.sortedComments(),
                        commentText = "",
                        isSendingComment = false,
                        commentError = null,
                    )
                )
            }.onFailure {
                _screenState.value = ScreenState.Content(
                    currentData.copy(
                        isSendingComment = false,
                        commentError = Res.string.comment_send_error,
                    )
                )
            }
        }
    }

    fun updateRsvp(status: AttendeeStatus) {
        val currentData = (_screenState.value as? ScreenState.Content)?.data ?: return
        if (currentData.rsvpLoadingStatus != null) return

        viewModelScope.launch {
            _screenState.value = ScreenState.Content(
                currentData.copy(rsvpLoadingStatus = status)
            )
            updateEventAttendeeUseCase(UpdateEventAttendeeRequest(eventId, status))
                .onSuccess { updatedEvent ->
                    _screenState.value = ScreenState.Content(
                        currentData.copy(
                            event = updatedEvent.sortedComments(),
                            rsvpLoadingStatus = null,
                        )
                    )
                }
                .onFailure {
                    _screenState.value = ScreenState.Content(
                        currentData.copy(rsvpLoadingStatus = null)
                    )
                }
        }
    }

    fun navigateBack() {
        navigationIntentChannel.trySend(NavigationEvent.Back)
    }

    fun currentUserStatus(event: Event): AttendeeStatus? {
        return event.attendees.firstOrNull { it.userId == currentUserId }?.status
    }

    fun isEventLive(event: Event): Boolean {
        val now = Clock.System.now().toEpochMilliseconds()
        return now in event.startsAt..event.endsAt
    }

    fun formatDate(timestamp: Long): String {
        val instant = Instant.fromEpochMilliseconds(timestamp)
        val dateTime = instant.toLocalDateTime(TimeZone.currentSystemDefault())
        val dayOfWeek =
            dateTime.dayOfWeek.name.lowercase().replaceFirstChar { it.uppercase() }.take(3)
        val month = dateTime.month.name.lowercase().replaceFirstChar { it.uppercase() }.take(3)
        val day = dateTime.day.toString().padStart(2, '0')
        val year = dateTime.year

        return "$dayOfWeek, $month $day $year"
    }

    fun formatTime(timestamp: Long): String {
        val instant = Instant.fromEpochMilliseconds(timestamp)
        val dateTime = instant.toLocalDateTime(TimeZone.currentSystemDefault())
        val hour = if (dateTime.hour % 12 == 0) 12 else dateTime.hour % 12
        val minute = dateTime.minute.toString().padStart(2, '0')
        val amPm = if (dateTime.hour < 12) "AM" else "PM"

        return "$hour:$minute $amPm"
    }

    fun formatCommentDateTime(timestamp: Long): String {
        return "${formatDate(timestamp)} at ${formatTime(timestamp)}"
    }

    private fun updateContent(update: (EventDetailsUiState) -> EventDetailsUiState) {
        val currentData = (_screenState.value as? ScreenState.Content)?.data ?: return
        _screenState.value = ScreenState.Content(update(currentData))
    }

    private fun Event.sortedComments(): Event {
        return copy(comments = comments.sortedByDescending { it.createdAt })
    }
}
